import { Box, Tooltip } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  LoadingIndicator,
  LocalesMenuButton,
  useLoading,
  usePermissions,
  useTranslate,
} from 'react-admin'
import { AlertMenuButton } from '../alerts'
import { EnvironmentBadge } from '../components/EnvironmentBadge'
import { GlucoseUnitMenuButton } from './GlucoseUnitMenuButton'

function LoadingIndicatorWithTimestamp() {
  const translate = useTranslate()
  const loading = useLoading()
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const wasLoading = useRef(false)

  useEffect(() => {
    if (wasLoading.current && !loading) {
      setLastFetchedAt(new Date())
    }
    wasLoading.current = loading
  }, [loading])

  const tooltip = lastFetchedAt
    ? translate('pos.app_bar.last_updated', { time: lastFetchedAt.toLocaleTimeString() })
    : translate('pos.app_bar.no_data_loaded')

  return (
    <Tooltip title={tooltip}>
      <span>
        <LoadingIndicator />
      </span>
    </Tooltip>
  )
}

export const AppBarToolbar = () => {
  const { permissions } = usePermissions()

  // Secondary actions (environment badge, theme swapper, loading indicator) are
  // hidden on phones (< sm) so the AppBar toolbar doesn't overflow the viewport;
  // they remain visible at sm+ (tablet/desktop) where there's room. The
  // functional essentials — alerts, language switcher, user menu — stay at every
  // width. `hideOnPhone` resolves to the prior inline rendering at sm+.
  const hideOnPhone = { display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center' } as const

  // The locale switcher prints the current language name ("English" / "中文"),
  // which is the widest toolbar control (~124px). On phones that label, stacked
  // with alerts + glucose unit + user menu, pushed the AppBar past a 390px
  // viewport — widening the whole RaLayout so every page scrolled horizontally
  // (caught by responsive-regression.spec.ts). Collapse the label to its
  // Translate icon on xs (fontSize:0 keeps the icon, drops the text) so the
  // switcher stays tappable but narrow; the full label returns at sm+, leaving
  // tablet/desktop untouched.
  // Only override BELOW the sm breakpoint (MUI sm = 600px). At sm+ no rule
  // applies, so the button keeps its natural font-size and full label — desktop
  // and tablet are provably untouched. (A responsive object with `sm: 'inherit'`
  // collapsed the label at every width, because `inherit` resolved to 0.)
  const localeIconOnlyOnPhone = {
    '& .MuiButton-root': {
      '@media (max-width:599.95px)': {
        fontSize: 0,
        minWidth: 0,
        '& .MuiButton-startIcon': { marginRight: 0 },
      },
    },
  } as const

  return (
    <>
      <Box sx={hideOnPhone}>
        <EnvironmentBadge />
      </Box>
      {/* Hide alerts for siteadmin (read-only access) */}
      {permissions?.role !== 'siteadmin' && <AlertMenuButton />}
      <Box component="span" sx={localeIconOnlyOnPhone}>
        <LocalesMenuButton />
      </Box>
      <GlucoseUnitMenuButton />
      <Box sx={hideOnPhone}>
        <LoadingIndicatorWithTimestamp />
      </Box>
    </>
  )
}
